export type SeoSchemaType = 'Article' | 'SoftwareApplication' | 'WebSite';

export interface SeoEntry {
  title: string;
  description: string;
  canonicalPath: string;
  type?: SeoSchemaType;
  image?: string;
  siteName?: string;
  tags?: string[];
}

export const SITE_URL = 'https://virtuoz-solutions.com';

export const defaultSeo: SeoEntry = {
  title: 'Virtuoz Solutions | Web Development and AI Services',
  description: 'Virtuoz Solutions builds web applications, AI integrations, automation workflows, and practical developer tooling from Angers, France.',
  canonicalPath: '/',
  type: 'WebSite',
};

export const seoByPath: Record<string, SeoEntry> = {
  '/': defaultSeo,
  '/blog': {
    title: 'Virtuoz Solutions Blog | AI, Web Development and Coding Tools',
    description: 'Practical articles about AI coding assistants, web development workflows, automation, and developer tooling.',
    canonicalPath: '/blog',
    type: 'WebSite',
  },
  '/portfolio': {
    title: 'Portfolio | Virtuoz Solutions',
    description: 'Selected web applications, AI products, and software projects built by Virtuoz Solutions.',
    canonicalPath: '/portfolio',
    type: 'WebSite',
  },
  '/contact': {
    title: 'Contact Virtuoz Solutions',
    description: 'Contact Virtuoz Solutions for web development, AI integration, automation, and software consulting projects.',
    canonicalPath: '/contact',
    type: 'WebSite',
  },
  '/about': {
    title: 'About Enzo Chevallier | Virtuoz Solutions',
    description: 'Learn about Enzo Chevallier, developer and founder of Virtuoz Solutions, focused on web development, AI, and automation.',
    canonicalPath: '/about',
    type: 'WebSite',
  },
  '/conseil-auto': {
    title: 'La Bonne Voiture | Conseil automobile indépendant',
    description: 'Shortlist personnalisée, comparaison du coût global et avis sur le mode d’acquisition pour choisir une voiture adaptée à vos usages.',
    canonicalPath: '/conseil-auto',
    type: 'WebSite',
    image: '/assets/la-bonne-voiture-hero.webp',
    siteName: 'La Bonne Voiture',
  },
  '/blog/ai-coding-assistants-windsurf-vs-cursor-vs-trae-whos-your-champion': {
    title: 'Trae vs Cursor vs Windsurf: AI Coding Assistant Comparison 2026',
    description: 'Compare Trae, Cursor and Windsurf for coding: agent workflows, IDE experience, codebase context, strengths, limits and best use cases.',
    canonicalPath: '/blog/ai-coding-assistants-windsurf-vs-cursor-vs-trae-whos-your-champion',
    type: 'Article',
    tags: ['Trae', 'Cursor', 'Windsurf', 'AI coding assistant', 'developer tools'],
  },
  '/blog/codex-vs-antigravity-2-0-vs-claude-code': {
    title: 'Codex vs Antigravity 2.0 vs Claude Code: Agentic Coding Tool Comparison',
    description: 'Compare Codex, Google Antigravity 2.0 and Claude Code for real software projects: workflows, codebase context, automation and team fit.',
    canonicalPath: '/blog/codex-vs-antigravity-2-0-vs-claude-code',
    type: 'Article',
    tags: ['Codex', 'Google Antigravity', 'Claude Code', 'AI coding agents'],
  },
  '/blog/claude-sonnet-4-claude-code-best-ai-development-2025': {
    title: 'Claude Sonnet 4 and Claude Code for Development: Practical Guide 2026',
    description: 'A practical guide to Claude Sonnet 4 and Claude Code for software teams: terminal workflows, codebase tasks, strengths, risks and use cases.',
    canonicalPath: '/blog/claude-sonnet-4-claude-code-best-ai-development-2025',
    type: 'Article',
    tags: ['Claude Sonnet 4', 'Claude Code', 'AI coding agent'],
  },
  '/blog/claude-37-sonnet-vs-gemini-15-pro-preview-which-ai-reigns-supreme-for-coding': {
    title: 'Claude 3.7 Sonnet vs Gemini 1.5 Pro for Coding: Developer Comparison',
    description: 'Compare Claude 3.7 Sonnet and Gemini 1.5 Pro for coding tasks, context windows, benchmark claims, agent workflows and practical use cases.',
    canonicalPath: '/blog/claude-37-sonnet-vs-gemini-15-pro-preview-which-ai-reigns-supreme-for-coding',
    type: 'Article',
    tags: ['Claude 3.7 Sonnet', 'Gemini 1.5 Pro', 'coding benchmarks'],
  },
  '/blog/google-jules-ai-agent-for-developers': {
    title: 'Google Jules AI Coding Agent: What Developers Should Know',
    description: 'A concise developer guide to Google Jules: asynchronous coding tasks, GitHub workflow, cloud VM execution, review flow and best use cases.',
    canonicalPath: '/blog/google-jules-ai-agent-for-developers',
    type: 'Article',
    tags: ['Google Jules', 'AI coding agent', 'Gemini'],
  },
  '/blog/meet-gemini-3-googles-latest-ai-powerhouse-thats-changing-the-game': {
    title: 'Gemini 3 for Developers: What Changed and Where It Fits',
    description: 'A developer-focused look at Gemini 3, coding workflows, multimodal use cases, strengths, limits and how it compares with other AI models.',
    canonicalPath: '/blog/meet-gemini-3-googles-latest-ai-powerhouse-thats-changing-the-game',
    type: 'Article',
    tags: ['Gemini 3', 'Google AI', 'developer tools'],
  },
  '/blog/so-what-makes-kimi-k2-thinking-different': {
    title: 'Kimi K2 Thinking: Agentic Reasoning, Tool Use and AI Gateway Workflows',
    description: 'Understand Kimi K2 Thinking, agentic reasoning, multi-step tool use, and when teams might route it through an AI gateway such as TrueFoundry.',
    canonicalPath: '/blog/so-what-makes-kimi-k2-thinking-different',
    type: 'Article',
    tags: ['Kimi K2 Thinking', 'Moonshot AI', 'TrueFoundry AI Gateway'],
  },
  '/portfolio/inkrify-instant-ai-ebook-creator': {
    title: 'Inkrify | Instant AI Ebook Creator',
    description: 'Inkrify is an AI ebook creator for drafting, structuring, editing and exporting long-form content faster.',
    canonicalPath: '/portfolio/inkrify-instant-ai-ebook-creator',
    type: 'SoftwareApplication',
  },
  '/portfolio/aplomb': {
    title: 'Aplomb | 2D and 3D Interior Design Studio',
    description: 'Draw rooms to scale, test furniture layouts in 2D and 3D, and export a complete interior design project pack directly in the browser.',
    canonicalPath: '/portfolio/aplomb',
    type: 'SoftwareApplication',
    image: '/uploads/aplomb-portfolio-hero-2026.webp',
  },
};

export const getSeoForPath = (pathname: string): SeoEntry => {
  const normalizedPath = pathname.endsWith('/') && pathname !== '/'
    ? pathname.slice(0, -1)
    : pathname;

  return seoByPath[normalizedPath] || {
    ...defaultSeo,
    canonicalPath: normalizedPath || '/',
  };
};
